import React, { useState, useMemo, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Check, X } from 'lucide-react';
import DashboardLayout from '../../components/DashboardLayout';
import UsersTable from './UsersTable';
import '../../styles/pages/newAlerts.css';
import '../../styles/pages/user-management.css';
import { UserManagementContext } from '../../contexts/UserManagementContext';
import { UserDto } from '../../services/userManagementApi';

type RoleFilter = 'All' | 'Admin' | 'Viewer';

/* ---- Permission Matrix row type ---- */
interface PermissionRow {
  feature: string;
  superAdmin: 'check' | 'cross' | 'request';
  admin: 'check' | 'cross' | 'request';
  viewer: 'check' | 'cross' | 'request';
}

const PERMISSIONS: PermissionRow[] = [
  { feature: 'View Dashboards',    superAdmin: 'check',   admin: 'check',   viewer: 'check' },
  { feature: 'Export Data',        superAdmin: 'check',   admin: 'check',   viewer: 'check' },
  { feature: 'Add/Modify Devices', superAdmin: 'check',   admin: 'request', viewer: 'cross' },
  { feature: 'Delete Devices',     superAdmin: 'check',   admin: 'check',   viewer: 'cross' },
  { feature: 'Delete Sites',       superAdmin: 'check',   admin: 'request', viewer: 'cross' },
  { feature: 'Manage Users',       superAdmin: 'check',   admin: 'cross',   viewer: 'cross' },
  { feature: 'Assign Roles',       superAdmin: 'check',   admin: 'cross',   viewer: 'cross' },
  { feature: 'API Keys',           superAdmin: 'check',   admin: 'check',   viewer: 'cross' },
  { feature: 'Subscription',       superAdmin: 'check',   admin: 'cross',   viewer: 'cross' },
];

const PermissionCell: React.FC<{ value: 'check' | 'cross' | 'request' }> = ({ value }) => {
  if (value === 'check') {
    return (
      <div className="um-perm-check">
        <Check size={14} strokeWidth={3} />
      </div>
    );
  }
  if (value === 'cross') {
    return (
      <div className="um-perm-cross">
        <X size={20} strokeWidth={4} />
      </div>
    );
  }
  return (
    <span className="um-perm-request">
      <span className="um-warn-icon">⚠️</span> Request
    </span>
  );
};

const UserManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const LoggedInUserData = JSON.parse(localStorage.getItem('userData') || '{}');
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('All');

  const context = useContext(UserManagementContext);
   if (!context) {
    throw new Error('useUserSelected must be used within a UserSelectedProvider');
  }

  const { Users, updateUser, getAllUsers, loading } = context;

  useEffect(() => {
    getAllUsers();
  }, []);

  /* ---- Derived counts ---- */
  const superAdminCount = Users.filter((u) => u.role === 'Super Admin').length;
  const adminCount      = Users.filter((u) => u.role === 'Admin').length;
  const viewerCount     = Users.filter((u) => u.role === 'Viewer').length;

  /* ---- Filtered Users — passed directly to AG Grid ---- */
  const filtered = useMemo(() => {
    return Users.filter((u) => {
      const matchesSearch =
        !searchTerm ||
        u.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.email.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesRole =
        roleFilter === 'All' ||
        (roleFilter === 'Admin' && (u.role === 'Admin' || u.role === 'Super Admin')) ||
        (roleFilter === 'Viewer' && u.role === 'Viewer');

      return matchesSearch && matchesRole;
    });
  }, [Users, searchTerm, roleFilter]);

  /* ---- Handlers ---- */
  const handleRoleChange = async (userId: number, role: UserDto['role']) => {
    const user = Users.find((u) => u.userId === userId);
    if (!user) return;
    await updateUser(userId, { firstName: user.firstName, email: user.email, role });
  };

  const handleStatusToggle = (_userId: number) => {
    // Status toggle not yet supported by API
  };


  const handleRoleFilterChange = (filter: RoleFilter) => {
    setRoleFilter(filter);
  };

  return (
    <DashboardLayout>
      <div className="um-container">

        {/* ---- Header ---- */}
        <div className="alerts-page-header zoomOutLevel">
          <div className="alerts-page-header-left">
            <h1 className="device-title">User Management</h1>
            <p className="alerts-subtitle device-subtitle ml-0">Manage individual user accounts</p>
          </div>

          <div className="alerts-page-header-right">
            {/* Search */}
            <div className="search-bar-container lg:min-w-[400px]">
              <input
                type="text"
                placeholder="Search by user name/role"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <div className="search-bar-icon">
                <Search className="w-5 h-5 text-white" />
              </div>
            </div>

            {/* Role Permissions button */}
            <button className="alerts-filter-button text-[var(--color-terms-link)]">Role Permissions</button>

            {/* Add New User button */}
            <button className="alerts-add-button" onClick={() => navigate('/add-user')}>+ Add New User</button>
          </div>
        </div>

        {/* ---- Top Section: Stats + Permission Matrix ---- */}
        <div className="um-top-section zoomOutLevel">

          {/* Stats Panel */}
          <div className="na-overview-left">
            <p className="na-section-label">OVERALL USERS</p>
            <div className="na-stats-grid">
              <div className="na-stat-card um-all-users">
                <div className="na-circle na-c1" />
                <div className="na-circle na-c2" />
                <p className="na-stat-label">All Users</p>
                <h2 className="na-stat-value">{String(Users.length).padStart(2, '0')}</h2>
              </div>
              <div className="na-stat-card um-super-admin">
                <div className="na-circle na-c1" />
                <div className="na-circle na-c2" />
                <p className="na-stat-label">Super Admin</p>
                <h2 className="na-stat-value">{String(superAdminCount).padStart(2, '0')}</h2>
              </div>
              <div className="na-stat-card um-admin">
                <div className="na-circle na-c1" />
                <div className="na-circle na-c2" />
                <p className="na-stat-label">Admin</p>
                <h2 className="na-stat-value">{String(adminCount).padStart(2, '0')}</h2>
              </div>
              <div className="na-stat-card um-viewer">
                <div className="na-circle na-c1" />
                <div className="na-circle na-c2" />
                <p className="na-stat-label">Viewer</p>
                <h2 className="na-stat-value">{String(viewerCount).padStart(2, '0')}</h2>
              </div>
            </div>
          </div>

          {/* Permission Matrix Panel */}
          <div className="um-permission-panel">
            <p className="na-section-label">PERMISSION MATRIX</p>
            <div className="um-table-wrap scrollbar">
              <table className="um-data-table">
                <thead>
                  <tr>
                    <th>Feature</th>
                    <th>Super Admin</th>
                    <th>Admin</th>
                    <th>Viewer</th>
                  </tr>
                </thead>
                <tbody>
                  {PERMISSIONS.map((row) => (
                    <tr key={row.feature}>
                      <td>{row.feature}</td>
                      <td><PermissionCell value={row.superAdmin} /></td>
                      <td><PermissionCell value={row.admin} /></td>
                      <td><PermissionCell value={row.viewer} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* ---- All Users Section ---- */}
        <div className="um-users-section">
          <div className="um-users-header zoomOutLevel">
            <p className="na-section-label" style={{ margin: 0 }}>ALL USERS</p>

            {/* Role filter tabs */}
            <div className="na-tabs">
              {(['All', 'Admin', 'Viewer'] as RoleFilter[]).map((tab) => (
                <button
                  key={tab}
                  className={`na-tab ${roleFilter === tab ? 'active' : ''}`}
                  onClick={() => handleRoleFilterChange(tab)}
                >
                  {tab}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="loading-container zoomOutLevel">
              <div className="h-8 w-8 border-b-2 border-[var(--gradient-button-left-color)] mb-4 animate-spin rounded-full"></div>
              <p className="text-base text-[var(--color-sub-title-text)]">
                Loading users...
              </p>
            </div>
          ) : (
            <UsersTable
              users={filtered}
              onRoleChange={handleRoleChange}
              onStatusToggle={handleStatusToggle}
              loggedInUserRole={LoggedInUserData.role}
            />
          )}
        </div>

      </div>

    </DashboardLayout>
  );
};

export default UserManagementPage;
